import { OptionModel } from '@/shared/lib/types'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import clsx from 'clsx'
import { ChevronDownIcon } from 'lucide-react'

type MobileTabsSelectProps<T extends string | number> = {
  tabs: OptionModel[]
  selectedTab: T
  onSelect: (tab: T) => void
  className?: string
  disabled?: boolean
}

export const MobileTabsSelect = <T extends string | number>({
  tabs,
  selectedTab,
  onSelect,
  className,
  disabled,
}: MobileTabsSelectProps<T>) => {
  const selected = tabs.find((tab) => tab.value === selectedTab) || tabs[0]

  const handleChange = (option: OptionModel) => {
    onSelect(option.value as T)
  }

  return (
    <div className={`flex flex-col gap-2 relative ${className || ''}`}>
      <Listbox value={selected} onChange={handleChange} disabled={disabled}>
        <ListboxButton
          className={clsx(
            'flex gap-2 items-center min-w-[180px] w-full text-left text-[28px] disabled:cursor-not-allowed disabled:opacity-75',
            'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-card-border'
          )}
        >
          <span>{selected.label}</span>
          <ChevronDownIcon className="group pointer-events-none w-6 h-6" aria-hidden="true" />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom"
          transition
          className={clsx(
            'w-[var(--button-width)] focus:outline-none',
            'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0'
          )}
        >
          {tabs
            .filter((option) => option.value !== selected.value)
            .map((option) => (
              <ListboxOption
                key={option.value}
                value={option}
                className={clsx(
                  'w-[var(--button-width)] rounded-lg ring-1 ring-inset p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none',
                  'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
                  'bg-white dark:bg-card-background ring-card-border dark:ring-card-border-dark group flex cursor-default items-center gap-2 rounded-lg py-1.5 select-none'
                )}
              >
                <div className={clsx('text-[28px]')}>{option.label}</div>
              </ListboxOption>
            ))}
        </ListboxOptions>
      </Listbox>
    </div>
  )
}
